import type { AnalyzerAnnotation, AnalyzerResult } from './types'

export interface ElementHealth {
  severity: 'error' | 'warning' | 'info' | 'success' | 'clean'
  count: number
  findings: AnalyzerAnnotation[]
}

export interface HealthSummary {
  errorCount: number
  warningCount: number
  infoCount: number
  cleanCount: number
  worst: { elementId: string; message: string } | null
  systemFindings: AnalyzerAnnotation[]
}

const SEVERITY_ORDER: Record<string, number> = {
  error: 0,
  warning: 1,
  info: 2,
  success: 3,
  clean: 4,
}

function worseSeverity(
  a: ElementHealth['severity'],
  b: ElementHealth['severity'],
): ElementHealth['severity'] {
  return (SEVERITY_ORDER[a] ?? 4) <= (SEVERITY_ORDER[b] ?? 4) ? a : b
}

export function computeElementHealthMap(
  results: Record<string, AnalyzerResult>,
): Record<string, ElementHealth> {
  const map: Record<string, ElementHealth> = {}

  for (const analyzerResult of Object.values(results)) {
    for (const annotation of analyzerResult.annotations) {
      if (!annotation.elementId) continue
      const id = annotation.elementId
      const existing = map[id]
      if (!existing) {
        map[id] = { severity: annotation.severity, count: 1, findings: [annotation] }
      } else {
        existing.severity = worseSeverity(existing.severity, annotation.severity)
        existing.count++
        existing.findings.push(annotation)
      }
    }
  }

  return map
}

export function computeHealthSummary(
  results: Record<string, AnalyzerResult>,
  healthMap: Record<string, ElementHealth>,
  totalElements: number,
): HealthSummary {
  let errorCount = 0
  let warningCount = 0
  let infoCount = 0
  const systemFindings: AnalyzerAnnotation[] = []

  for (const entry of Object.values(healthMap)) {
    for (const finding of entry.findings) {
      if (finding.severity === 'error') errorCount++
      else if (finding.severity === 'warning') warningCount++
      else if (finding.severity === 'info') infoCount++
    }
  }

  for (const analyzerResult of Object.values(results)) {
    for (const annotation of analyzerResult.annotations) {
      if (!annotation.elementId) {
        systemFindings.push(annotation)
        if (annotation.severity === 'error') errorCount++
        else if (annotation.severity === 'warning') warningCount++
        else if (annotation.severity === 'info') infoCount++
      }
    }
  }

  let worst: HealthSummary['worst'] = null
  let worstOrder = Infinity

  for (const analyzerResult of Object.values(results)) {
    for (const annotation of analyzerResult.annotations) {
      if (!annotation.elementId) continue
      const order = SEVERITY_ORDER[annotation.severity] ?? 4
      if (order < worstOrder) {
        worstOrder = order
        worst = { elementId: annotation.elementId, message: annotation.message }
      }
    }
  }

  const cleanCount = totalElements - Object.keys(healthMap).length

  return { errorCount, warningCount, infoCount, cleanCount, worst, systemFindings }
}
